import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import { useLunchSummary, useUpdateLunchPaymentPaid } from '../../hooks/useLunch';
import PaymentQRCode from '../../components/PaymentQRCode';

const LunchSummary: React.FC = () => {
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);

  const { data: summary, isLoading } = useLunchSummary(selectedYear, selectedMonth);
  const updatePaymentPaid = useUpdateLunchPaymentPaid();

  const handlePaidToggle = async (employeeId: string, currentPaidStatus: boolean) => {
    try {
      await updatePaymentPaid.mutateAsync({
        employeeId,
        year: selectedYear,
        month: selectedMonth,
        paid: !currentPaidStatus
      });
    } catch (error) {
      console.error('Failed to update paid status:', error);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const totalAmount = summary?.reduce((sum, item) => sum + item.totalAmount, 0) || 0;
  const totalOrders = summary?.reduce((sum, item) => sum + item.orderCount, 0) || 0;

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            {/* Left side: Monthly summary */}
            <Box sx={{ flex: '1 1 300px', minWidth: 250, maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', width: '100%' }}>
                Monthly Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center', width: '100%' }}>
                View total lunch expenses for each employee by month
              </Typography>
              <FormControl sx={{ minWidth: 200, width: '100%' }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200, width: '100%' }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Paper sx={{ p: 2, bgcolor: 'action.hover', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Total Orders:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {totalOrders}
                  </Typography>
                </Box>
              </Paper>
              <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Total Amount:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {totalAmount} RSD
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* Right side: QR Code */}
            <Box sx={{ flex: '1 1 300px', minWidth: 250, maxWidth: 400, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
              <PaymentQRCode />
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !summary || summary.length === 0 ? (
            <Alert severity="info">
              No orders found for this month.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Employee</strong></TableCell>
                    <TableCell align="center"><strong>Days Ordered</strong></TableCell>
                    <TableCell align="center"><strong>Paid</strong></TableCell>
                    <TableCell align="right"><strong>Total Amount</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.map((item) => (
                    <TableRow key={item.employeeId} hover>
                      <TableCell>
                        {item.firstName} {item.lastName}
                      </TableCell>
                      <TableCell align="center">
                        {item.orderCount}
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={item.paid || false}
                          onChange={() => handlePaidToggle(item.employeeId, item.paid || false)}
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {item.totalAmount} RSD
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle1" fontWeight="bold">
                        {totalOrders}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" />
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        {totalAmount} RSD
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LunchSummary;
